/**
 * @file Shared canonical sort helpers for top-level items and embedded task lists.
 */

import { appData } from "../../app/state/state.js";
import {
    findItem,
    getAssetDisplayDate,
    getItemDisplayName,
    getNextActionDate,
    getNextDueDate,
} from "../utils/itemLookup.js";
import {
    getPatientProviderDisplayNames,
} from "../utils/providerUtils.js";
import {
    getUserDisplayNameById,
} from "../utils/userDisplay.js";
import {
    soonestDate,
} from "../utils/dateUtils.js";
import {
    isItemClosed,
} from "../utils/closedState.js";

function getStatusField(itemType) {
  return itemType === "asset" ? "equipment_status" : "status";
}

function getStatusList(itemType) {
  switch (itemType) {
    case "task":
      return appData?.config?.taskStatus || [];
    case "patient":
      return appData?.config?.patientStatus || [];
    case "project":
      return appData?.config?.projectStatus || [];
    case "asset":
      return appData?.config?.equipmentStatus || [];
    case "lab":
      return appData?.config?.labStatus || [];
    case "maintenance":
      return appData?.config?.maintenanceStatus || [];
    default:
      return [];
  }
}

function getTypeConfig(itemType) {
  switch (itemType) {
    case "task":
      return appData?.config?.taskTypes || [];
    case "patient":
      return appData?.config?.patientTypes || [];
    case "project":
      return appData?.config?.projectTypes || [];
    case "asset":
      return appData?.config?.assetTypes || [];
    case "lab":
      return appData?.config?.labTypes || [];
    case "maintenance":
      return appData?.config?.maintenanceTypes || [];
    default:
      return [];
  }
}

function getPriorityOrder(item) {
  const priorities = appData?.config?.priorities || [];
  const priority = priorities.find((entry) => entry.id === item?.priority);
  return priority ? Number(priority.order ?? 999) : 999;
}

function getStatusOrder(itemType, item) {
  const statusField = getStatusField(itemType);
  const statusList = getStatusList(itemType);
  const status = statusList.find((entry) => entry.id === item?.[statusField]);
  return status ? Number(status.order ?? 999) : 999;
}

function parseDateValue(dateValue) {
  if (!dateValue) {return null;}

  try {
    const parsedDate = new Date(dateValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  } catch {
    return null;
  }
}

function getUpdatedAtValue(item) {
  const updatedAt = item?.updatedAt;
  if (!updatedAt) {return null;}

  if (typeof updatedAt?.toMillis === "function") {
    return updatedAt.toMillis();
  }
  if (typeof updatedAt?.seconds === "number") {
    const nanos = typeof updatedAt?.nanoseconds === "number" ?
      updatedAt.nanoseconds : 0;
    return (updatedAt.seconds * 1000) + Math.floor(nanos / 1000000);
  }

  const parsedDate = parseDateValue(updatedAt);
  return parsedDate ? parsedDate.getTime() : null;
}

function compareNullableValues(valueA, valueB, compareFn) {
  const hasA = valueA !== null && valueA !== undefined;
  const hasB = valueB !== null && valueB !== undefined;

  if (!hasA && !hasB) {return 0;}
  if (!hasA) {return 1;}
  if (!hasB) {return -1;}

  return compareFn(valueA, valueB);
}

function compareStrings(valueA, valueB) {
  return String(valueA || "").localeCompare(String(valueB || ""));
}

function compareNumbers(valueA, valueB) {
  return Number(valueA) - Number(valueB);
}

function getSortedNonEmptyStrings(values = []) {
  return (Array.isArray(values) ? values : [])
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

function getPrimarySortedUserDisplayName(userIds = []) {
  const sortedNames = getSortedNonEmptyStrings(
    (Array.isArray(userIds) ? userIds : []).map((userId) =>
      userId ? getUserDisplayNameById(userId) : "",
    ),
  );
  return sortedNames[0] || null;
}

function getPrimarySortedProviderDisplayName(providerIds = []) {
  const sortedNames = getSortedNonEmptyStrings(
    getPatientProviderDisplayNames(providerIds),
  );
  return sortedNames[0] || null;
}

function getMaintenanceDisplayDate(item, taskList = appData?.tasks || []) {
  if (!item?.id) {return null;}
  if (isItemClosed(item)) {
    return item.completed_on || null;
  }

  return soonestDate(
    getNextDueDate(item.id, taskList),
    item.due_date || null,
  );
}

function getMaintenanceTechnicianSortValue(item) {
  const technician = String(item?.technician || "").trim();
  if (!technician) {return "zzzz_none";}

  const configured = (appData?.config?.maintenance?.technicians || [])
    .filter((entry) => entry?.isActive !== false);
  const match = configured.find((entry) => entry?.name === technician);
  if (match) {
    return `${String(match.order ?? 999).padStart(4, "0")}_${technician}`;
  }
  return `zzzy_${technician}`;
}

function getLabSortValue(item) {
  const labId = String(item?.lab || "");
  if (labId === "internal") {
    return `0000_${appData?.config?.labs?.internalLabLabel || "Internal lab"}`;
  }
  const lab = (appData?.config?.labs?.externalLabs || [])
    .find((entry) => entry?.id === labId);
  if (lab) {
    return `${String(lab.order ?? 999).padStart(4, "0")}_${lab.name || ""}`;
  }
  return labId ? `zzzy_${labId}` : "zzzz_none";
}

/**
 * Resolves the canonical date used to sort or group an item card.
 * @param {string} itemType Parent-board item type.
 * @param {object} item Item record.
 * @param {Array<object>} [taskList=appData?.tasks || []] Related task list.
 * @returns {string | null} Resolved date string, or null.
 */
export function getResolvedCardDate(itemType, item, taskList = appData?.tasks || []) {
  switch (itemType) {
    case "task":
      return item?.due_date || null;
    case "patient":
      return soonestDate(
        getNextActionDate(item?.id, taskList),
        item?.next_appointment || null,
      );
    case "project":
      return soonestDate(
        getNextDueDate(item?.id, taskList),
        item?.delivery_date || null,
      );
    case "asset":
      return getAssetDisplayDate(item, taskList);
    case "maintenance":
      return getMaintenanceDisplayDate(item, taskList);
    case "lab":
      return item?.due_date || null;
    default:
      return null;
  }
}

/**
 * Resolves the stable sort value for an item's configured types.
 * @param {string} itemType Parent-board item type.
 * @param {object} item Item record.
 * @returns {string} Sortable type value.
 */
export function getTypeSortValue(itemType, item) {
  const typeConfig = getTypeConfig(itemType);
  const types = Array.isArray(item?.types) ? item.types : [];
  if (types.length === 0) {return "zzzz_none";}

  const sortedTypes = [...types]
    .map((typeId) => typeConfig.find((entry) => entry.id === typeId))
    .filter(Boolean)
    .sort((a, b) => {
      const orderDiff = (a.order ?? 999) - (b.order ?? 999);
      if (orderDiff !== 0) {return orderDiff;}
      return (a.name || "").localeCompare(b.name || "");
    });

  if (sortedTypes.length > 0) {
    const firstType = sortedTypes[0];
    return `${String(firstType.order ?? 999).padStart(4, "0")}_${firstType.name || ""}`;
  }

  return `zzzy_${types[0] || ""}`;
}

function getFieldSortValue(itemType, item, field, taskList) {
  switch (field) {
    case "priority":
      return getPriorityOrder(item);
    case "status":
    case "equipment_status":
      return getStatusOrder(itemType, item);
    case "types":
      return getTypeSortValue(itemType, item);
    case "date":
      return getResolvedCardDate(itemType, item, taskList);
    case "stream":
      if (itemType === "lab") {
        return item?.stream === "external" ? 0 :
          item?.stream === "internal" ? 1 :
          2;
      }
      return item?.stream === "issue" ? 0 : item?.stream === "service" ? 1 : 2;
    case "lab":
      return itemType === "lab" ? getLabSortValue(item) : item?.lab || "";
    case "technician":
      return itemType === "maintenance" ?
        getMaintenanceTechnicianSortValue(item) :
        item?.technician || "";
    case "managed_by":
      return item?.managed_by ? getUserDisplayNameById(item.managed_by) : null;
    case "due_date":
    case "completed_on":
    case "next_appointment":
    case "delivery_date":
    case "next_service_due":
    case "purchase_date":
    case "first_seen":
      return item?.[field] || null;
    case "next_action_required":
      return getNextActionDate(item?.id, taskList);
    case "next_due_date":
      return getNextDueDate(item?.id, taskList);
    case "case_value":
    case "cost":
      return Number.isFinite(Number(item?.[field])) ? Number(item[field]) : null;
    case "owner":
    case "case_manager":
      return item?.[field] ? getUserDisplayNameById(item[field]) : null;
    case "assigned_users":
      return getPrimarySortedUserDisplayName(item?.assigned_users);
    case "project_manager":
      return getPrimarySortedUserDisplayName(item?.project_manager);
    case "provider":
      return getPrimarySortedProviderDisplayName(item?.provider);
    case "parent": {
      const parentItem = findItem(item?.parentId);
      return getItemDisplayName(parentItem, item?.parentId || "");
    }
    default:
      return item?.[field] ?? "";
  }
}

function compareFieldValues(field, valueA, valueB) {
  if (
    field === "priority" ||
    field === "status" ||
    field === "equipment_status" ||
    field === "case_value" ||
    field === "cost" ||
    field === "stream"
  ) {
    return compareNullableValues(valueA, valueB, compareNumbers);
  }

  if (
    field === "date" ||
    field === "due_date" ||
    field === "completed_on" ||
    field === "next_appointment" ||
    field === "delivery_date" ||
    field === "next_service_due" ||
    field === "purchase_date" ||
    field === "first_seen" ||
    field === "next_action_required" ||
    field === "next_due_date"
  ) {
    const dateA = parseDateValue(valueA);
    const dateB = parseDateValue(valueB);
    return compareNullableValues(
      dateA?.getTime() ?? null,
      dateB?.getTime() ?? null,
      compareNumbers,
    );
  }

  if (
    field === "owner" ||
    field === "case_manager" ||
    field === "assigned_users" ||
    field === "project_manager" ||
    field === "provider" ||
    field === "managed_by" ||
    field === "technician"
  ) {
    return compareNullableValues(valueA, valueB, compareStrings);
  }

  if (typeof valueA === "number" && typeof valueB === "number") {
    return compareNumbers(valueA, valueB);
  }

  return compareStrings(valueA, valueB);
}

/**
 * Compares parent-board items by the canonical board ordering.
 * @param {string} itemType Parent-board item type.
 * @param {object} a First item.
 * @param {object} b Second item.
 * @param {Array<object>} [taskList=appData?.tasks || []] Related task list.
 * @returns {number} Sort comparison result.
 */
export function compareCanonicalItems(itemType, a, b, taskList = appData?.tasks || []) {
  const hasPriorityTier = itemType !== "asset";

  if (hasPriorityTier) {
    const priorityDiff = getPriorityOrder(a) - getPriorityOrder(b);
    if (priorityDiff !== 0) {return priorityDiff;}
  }

  const dateA = parseDateValue(getResolvedCardDate(itemType, a, taskList));
  const dateB = parseDateValue(getResolvedCardDate(itemType, b, taskList));
  const dateDiff = compareNullableValues(
    dateA?.getTime() ?? null,
    dateB?.getTime() ?? null,
    compareNumbers,
  );
  if (dateDiff !== 0) {return dateDiff;}

  const statusDiff = getStatusOrder(itemType, a) - getStatusOrder(itemType, b);
  if (statusDiff !== 0) {return statusDiff;}

  const updatedAtDiff = compareNullableValues(
    getUpdatedAtValue(a),
    getUpdatedAtValue(b),
    compareNumbers,
  );
  if (updatedAtDiff !== 0) {return updatedAtDiff;}

  return compareStrings(a?.id || "", b?.id || "");
}

/**
 * Compares top-level board items by a selected field with canonical fallback.
 * @param {string} itemType Parent-board item type.
 * @param {object} a First item.
 * @param {object} b Second item.
 * @param {string} field Sort field.
 * @param {"asc" | "desc"} [direction="asc"] Sort direction.
 * @param {Array<object>} [taskList=appData?.tasks || []] Related task list.
 * @returns {number} Sort comparison result.
 */
export function compareTopLevelItemsByField(
  itemType,
  a,
  b,
  field,
  direction = "asc",
  taskList = appData?.tasks || [],
) {
  if (!field) {
    return compareCanonicalItems(itemType, a, b, taskList);
  }

  const valueA = getFieldSortValue(itemType, a, field, taskList);
  const valueB = getFieldSortValue(itemType, b, field, taskList);
  const baseComparison = compareFieldValues(field, valueA, valueB);

  if (baseComparison !== 0) {
    return direction === "desc" ? -baseComparison : baseComparison;
  }

  return compareCanonicalItems(itemType, a, b, taskList);
}

/**
 * Compares task items by the canonical board ordering.
 * @param {object} a First task.
 * @param {object} b Second task.
 * @param {Array<object>} [taskList=appData?.tasks || []] Related task list.
 * @returns {number} Sort comparison result.
 */
export function compareCanonicalTaskItems(a, b, taskList = appData?.tasks || []) {
  return compareCanonicalItems("task", a, b, taskList);
}

/**
 * Sorts board items by a selected field without mutating the input array.
 * @param {string} itemType Parent-board item type.
 * @param {Array<object>} [items=[]] Items to sort.
 * @param {string} field Sort field.
 * @param {"asc" | "desc"} [direction="asc"] Sort direction.
 * @param {Array<object>} [taskList=appData?.tasks || []] Related task list.
 * @returns {Array<object>} Sorted items.
 */
export function sortItemsByField(
  itemType,
  items = [],
  field,
  direction = "asc",
  taskList = appData?.tasks || [],
) {
  return [...(items || [])].sort((a, b) => compareTopLevelItemsByField(
    itemType,
    a,
    b,
    field,
    direction,
    taskList,
  ));
}

/**
 * Sorts tasks by canonical board ordering without mutating the input array.
 * @param {Array<object>} [tasks=[]] Task records.
 * @param {Array<object>} [taskList=appData?.tasks || []] Related task list.
 * @returns {Array<object>} Sorted tasks.
 */
export function sortCanonicalTaskItems(tasks = [], taskList = appData?.tasks || []) {
  return [...(tasks || [])].sort((a, b) => compareCanonicalTaskItems(a, b, taskList));
}
